/**
 * Thin API client for the SEYON Pulse admin console.
 *
 * Token handling, and why it looks like this:
 *
 * The access token is held in a module variable — never in `localStorage`. An
 * admin session can read PHI, so a single XSS on this origin should not be able
 * to walk away with a long-lived credential from disk.
 *
 * The refresh token sits in `sessionStorage`, which survives a page reload but
 * dies with the tab and is not shared across tabs. That is a deliberate middle
 * ground, not the ideal: the ideal is the API setting an `HttpOnly; Secure;
 * SameSite=Strict` refresh cookie, which `/v1/auth/*` does not currently issue.
 * When it does, delete `REFRESH_KEY` and this comment.
 */

const REFRESH_KEY = 'seyon.admin.refresh'
const BASE = import.meta.env.VITE_API_BASE ?? ''

let accessToken: string | null = null
let refreshInFlight: Promise<boolean> | null = null

export type SessionUser = {
  user_id: string
  display_name: string
  email: string
  roles: string[]
  is_new_user?: boolean
  /** Still on the temporary password an admin issued at creation. */
  must_change_password?: boolean
}

export type TokenResponse = {
  access_token: string
  refresh_token: string
  expires_in: number
  user: SessionUser
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** 403 from `require(...)` — the actor is authenticated but lacks a permission. */
  get isForbidden(): boolean {
    return this.status === 403
  }
}

export function setSession(tokens: TokenResponse): void {
  accessToken = tokens.access_token
  sessionStorage.setItem(REFRESH_KEY, tokens.refresh_token)
}

export function clearSession(): void {
  accessToken = null
  sessionStorage.removeItem(REFRESH_KEY)
}

export function hasStoredRefresh(): boolean {
  return sessionStorage.getItem(REFRESH_KEY) !== null
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = await response.json()
    if (typeof body?.detail === 'string') return body.detail
    if (Array.isArray(body?.detail)) {
      // FastAPI validation errors: surface the field, not the whole blob.
      return body.detail
        .map((d: { loc?: unknown[]; msg?: string }) =>
          `${(d.loc ?? []).slice(1).join('.')}: ${d.msg ?? 'invalid'}`,
        )
        .join('; ')
    }
    return response.statusText || `HTTP ${response.status}`
  } catch {
    return response.statusText || `HTTP ${response.status}`
  }
}

/** Exchange the stored refresh token for a new pair. Deduplicated: a burst of
 *  401s from parallel requests must not spend the single-use token more than
 *  once, or the backend's reuse detection revokes the whole family. */
async function refresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const stored = sessionStorage.getItem(REFRESH_KEY)
    if (!stored) return false
    try {
      const response = await fetch(`${BASE}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: stored }),
      })
      if (!response.ok) {
        clearSession()
        return false
      }
      setSession((await response.json()) as TokenResponse)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

type RequestOptions = {
  method?: string
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  signal?: AbortSignal
}

async function send(path: string, options: RequestOptions, retry: boolean): Promise<Response> {
  const url = new URL(`${BASE}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value))
  }

  const headers: Record<string, string> = {}
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  const response = await fetch(url.toString().replace(window.location.origin, ''), {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (response.status === 401 && retry && (await refresh())) {
    return send(path, options, false)
  }
  return response
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options, true)
  if (!response.ok) throw new ApiError(response.status, await parseError(response))
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }
  return (await response.json()) as T
}

export const get = <T,>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
  api<T>(path, { query, signal })

export const post = <T,>(path: string, body?: unknown, query?: RequestOptions['query']) =>
  api<T>(path, { method: 'POST', body, query })

export const put = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body })

/** Partial update. Only the fields present are sent, so two operators editing
 *  different fields of the same record do not overwrite each other. */
export const patch = <T,>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PATCH', body })

export const del = <T,>(path: string, query?: RequestOptions['query']) =>
  api<T>(path, { method: 'DELETE', query })

export async function login(email: string, password: string): Promise<TokenResponse> {
  const response = await fetch(`${BASE}/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!response.ok) throw new ApiError(response.status, await parseError(response))
  const tokens = (await response.json()) as TokenResponse
  setSession(tokens)
  return tokens
}

export async function restore(): Promise<TokenResponse | null> {
  if (!hasStoredRefresh()) return null
  const stored = sessionStorage.getItem(REFRESH_KEY)
  const response = await fetch(`${BASE}/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: stored }),
  })
  if (!response.ok) {
    clearSession()
    return null
  }
  const tokens = (await response.json()) as TokenResponse
  setSession(tokens)
  return tokens
}

export async function logout(): Promise<void> {
  try {
    await api('/v1/auth/logout', { method: 'POST' })
  } finally {
    clearSession()
  }
}
